import { useState } from 'react'
import type { FamilySession } from '@core/session'
import { Button, Card, Screen } from '@design/components'
import { AddScreen } from './AddScreen'
import { UploadScreen } from './UploadScreen'
import { ExpenseForm } from '@features/expenses/ExpenseForm'
import { loadSharedSpaces, type SharedSpace } from '@core/expenses'
import { useEffect } from 'react'

/**
 * The + button, which turns out to mean two different things.
 *
 * "I have the invoice in my hand" and "I know what I owe but have no
 * document" are genuinely different tasks, and merging them into one form
 * makes both worse — the upload would carry fields nobody needs, and the
 * manual entry would carry a file picker nobody has anything to put in.
 *
 * Uploading is first and larger because it is the common case: almost every
 * payment starts as a piece of paper or a PDF.
 */
type Mode = 'choose' | 'upload' | 'manual' | 'expense'

export function AddTab({
  session,
  onDone,
  onClose,
}: {
  session: FamilySession
  onDone: () => void
  onClose: () => void
}) {
  const [mode, setMode] = useState<Mode>('choose')
  const [shared, setShared] = useState<SharedSpace[]>([])

  // The expense route only appears once a space exists for it. Offering it on
  // a family with two spaces called Huis and Zaak would be a button with
  // nowhere sensible to put the receipt.
  useEffect(() => {
    loadSharedSpaces()
      .then(setShared)
      .catch(() => setShared([]))
  }, [])

  if (mode === 'upload') {
    return <UploadScreen session={session} onDone={onDone} onClose={() => setMode('choose')} />
  }

  if (mode === 'expense' && shared[0]) {
    const space = session.spaces.find((s) => s.id === shared[0]!.id)
    if (space) {
      return (
        <ExpenseForm
          space={space}
          userId={session.userId}
          onDone={onDone}
          onCancel={() => setMode('choose')}
        />
      )
    }
  }

  if (mode === 'manual') {
    return <AddScreen session={session} onDone={onDone} onClose={() => setMode('choose')} />
  }

  return (
    <Screen>
      <Card className="mb-3">
        <p className="font-medium mb-1">Foto of pdf</p>
        <p className="text-sm text-muted mb-3">
          Een factuur die je in handen hebt, of een pdf uit je mail. Wij lezen
          het bedrag en het rekeningnummer eruit.
        </p>
        <Button size="large" onClick={() => setMode('upload')}>
          Bestand kiezen
        </Button>
      </Card>

      <Card className="mb-3">
        <p className="font-medium mb-1">Zelf invullen</p>
        <p className="text-sm text-muted mb-3">
          Geen document, maar je weet wat er betaald moet worden.
        </p>
        <Button tone="secondary" onClick={() => setMode('manual')}>
          Betaling invullen
        </Button>
      </Card>

      {shared.length > 0 && (
        <Card className="mb-3">
          <p className="font-medium mb-1">Uitgave in {shared[0]!.name}</p>
          <p className="text-sm text-muted mb-3">
            Iets dat je al betaald hebt. Bedrag tikken, bonnetje erbij, klaar.
          </p>
          <Button tone="secondary" onClick={() => setMode('expense')}>
            Uitgave toevoegen
          </Button>
        </Card>
      )}

      <Button tone="quiet" onClick={onClose}>
        Terug
      </Button>
    </Screen>
  )
}
